import { Component } from 'react'
import cn from 'clsx'

// Components
import { IconCheckmark, IconCrossCircle } from '@tracklib/kit'

// Types
import type { LicenseSelectorInfo } from 'selectors'

import styles from './checklist.css'

type LicenseChecklistProps = {
  licenseInfo: LicenseSelectorInfo
  syncAllowed: boolean
}

const LicenseChecklist = ({ licenseInfo: { descriptionShort, sharePercentage }, syncAllowed }: LicenseChecklistProps): JSX.Element => {
  return (
    <Checklist // eslint-disable-line
      categories={[
        {
          title: 'What you can do',
          lines: [
            {
              checked: true,
              text: `Sample ${descriptionShort.toLowerCase()} from the original song and use it however you want in your new song (but not separate from the new song).`,
            },
            {
              checked: true,
              text: 'Exploit your new song worldwide, without limit in time and via all media.',
            },
          ],
        },
        {
          title: 'What you cannot do',
          lines: [
            {
              checked: false,
              text: 'Simply create a remix of the original song – your new song must be substantially different from the original.',
            },
            {
              checked: false,
              text: 'Further sample the original song into any other song than the one specified here.',
            },
            ...(!syncAllowed
              ? [
                  {
                    checked: false,
                    highlight: true,
                    text: 'To sync your new track for commercial use (in adverts, video games, film etc) you have to request permission.',
                  },
                ]
              : []),
          ],
        },
        {
          title: 'What you must do',
          lines: [
            {
              checked: true,
              text: 'Comply with all the provisions of the sample license so please read it carefully.',
            },
            {
              checked: true,
              text: `Account for and pay a ${sharePercentage}% royalty on all income.`,
            },
            {
              checked: true,
              text: 'Register your new song with appropriate collective rights management organisations.',
            },
          ],
        },
      ]}
    />
  )
}

type CheckListProps = {
  categories: Array<{
    title: string
    lines: Array<{
      checked: boolean
      highlight?: boolean
      text: string
    }>
  }>
}

class Checklist extends Component<CheckListProps> {
  static License: typeof LicenseChecklist
  shouldComponentUpdate(nextProps: CheckListProps) {
    if (this.props.categories.length !== nextProps.categories.length) {
      return true
    }
    return false
  }

  render() {
    const { categories } = this.props
    return (
      <ul className={styles.list}>
        {categories.map(({ title, lines }) => (
          <li key={title}>
            <h2 className={styles.title}>{title}</h2>
            <ul className={styles.list}>
              {lines.map(({ checked, highlight, text }) => (
                <li key={text} className={cn(styles.item, highlight && (checked ? styles.checkedHighlight : styles.uncheckedHighlight))}>
                  <div className={styles.icon}>{checked ? <IconCheckmark /> : <IconCrossCircle />}</div>
                  <p className={styles.text}>{text}</p>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    )
  }
}

Checklist.License = LicenseChecklist

export default Checklist
